/** @jsxImportSource theme-ui */
import React from "react";
import { useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import { globalHistory as history } from "@reach/router";
import { Box, Flex, Link as Href, Image, Text } from "@theme-ui/components";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import Menu, {
  Divider,
  Item as MenuItem,
  SubMenu,
  ItemGroup as MenuItemGroup,
} from "rc-menu";
import { navigate } from "gatsby";
import { navigateCrossApp } from "@greythr/shared-utils/navigate-cross-app";
import { slide as SlideMenu } from "react-burger-menu";
import { useSelector, useDispatch } from "react-redux";
import { uid } from "react-uid";
import "../../styles/menu.less";
import { Accordion } from "../organisms";
import { ListCard } from "../molecules";
import crossIcon from "../../images/cross-menu.svg";
import burgerMenuIcon from "../../images/burger-menu.svg";
import logo from "../../images/greythr-logo.svg";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { setPageRegion } from "../../../../shared-store/src/components/state/app";
import { SUPPORTED_REGIONS } from "../../../../shared-utils/src/components/utils/region";
import { getCommonData } from "../../../../shared-utils/src/components/utils/regionContent";
import { Button, NavLink, RegionalLink } from "../atoms";
import { colors, typography } from "../../styles/theme";
import NavosHeroActions from "../molecules/NavosHeroActions";

const collapseNode = () => ({ opacity: 0 });
const expandNode = (node) => ({ opacity: 1 });

const inlineMotion = {
  motionName: "rc-menu-collapse",
  onAppearStart: collapseNode,
  onAppearActive: expandNode,
  onEnterStart: collapseNode,
  onEnterActive: expandNode,
  onLeaveStart: expandNode,
  onLeaveActive: collapseNode,
};

const StyledHeaderContainer = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  flex-wrap: no-wrap;
  min-height: 5rem;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1030;
  padding-right: 1.06rem;
  padding-left: 1.06rem;
  border-radius: 0 0 1.25rem 1.25rem;
  @media (min-width: 600px) {
    padding-right: 2rem;
    padding-left: 2rem;
  }
  @media (min-width: 974px) {
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
  @media (min-width: 1157px) {
    padding-right: 2rem;
    padding-left: 2rem;
  }
  @media (min-width: 1440px) {
    padding-right: 2rem;
    padding-left: 2rem;
  }
`;

const StyledHeaderNav = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  font-weight: 400;
  width: 100%;
  py: 0.75rem;
  gap: 2rem;
`;

const burgerMenuStyles = {
  bmBurgerButton: {
    width: "1.813rem",
    height: "1.813rem",
    position: "relative",
    padding: 0,
    marginRight: -30,
  },
  bmCrossButton: {
    height: "32px",
    width: "32px",
    top: "1.5rem",
    right: "1rem",
  },
  bmCross: {
    height: "32px",
    width: "32px",
  },
  bmMenuWrap: {
    position: "fixed",
    height: "100%",
    width: "100%",
    backgroundColor: colors.white,
    top: 0,
  },
  bmMenu: {
    padding: "0 1rem",
    fontSize: "1.15em",
    height: "76%",
    width: "100%",
    backgroundColor: colors.white,
  },
  bmMorphShape: {
    fill: colors.primary100,
  },
  bmItemList: {
    color: colors.white,
  },
  bmItem: {
    display: "block",
  },
  bmOverlay: {
    background: colors.black,
    opacity: 0.4,
    overflow: "hidden",
  },
};

const promoStyles = {
  contentContainer: {
    maxWidth: "213px",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    "@media(max-width: 1024px)": {
      flexDirection: "row",
      alignItems: "flex-start",
      maxWidth: "100%",
    },
  },
};

const styledMenu = {
  display: "flex",
  alignItems: "center",
  backgroundColor: colors.white,
  gap: ["1rem", "1rem", "1rem", "0.1rem"],
};

const burgerMenuItemsStyles = {
  divider: {
    width: "100%",
    height: "1px",
    backgroundColor: colors.neutral200,
    my: "1rem",
  },
  promoSection: {
    position: "absolute",
    bottom: 0,
    p: "0rem",
    left: "1rem",
    right: "1rem",
    bottom: "2rem",
  },
  logoContainer: {
    display: "flex !important",
    position: "absolute",
    height: "5rem",
    left: "1rem",
    right: "1rem",
    width: "100%",
    top: 0,
    backgroundColor: colors.white,
    alignItems: "center",
  },
};

const linkStyles = {
  ...typography.h7,
  textDecoration: "none",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "none",
    backgroundColor: colors.primary100,
    color: colors.primaryMain,
  },
  p: "1rem",
};

const PromoSection = ({
  menuData,
  innerCustomStyles,
  textInnerContainerCustomStyles,
  textContainerCustomStyles,
  customStyles,
}) => {
  const handleClick = () => {
    window.open(menuData?.url, "newwindow");
  };
  return (
    <Flex
      sx={{
        py: "2.23rem",
        px: "3.16rem",
        backgroundColor: colors.white,
        ...customStyles,
      }}
    >
      <Flex sx={{ ...promoStyles.contentContainer, ...innerCustomStyles }}>
        {menuData?.image && (
          <GatsbyImage
            image={getImage(menuData?.image)}
            alt={menuData?.title}
            style={{ borderRadius: "0.4375rem", minWidth: "11.5rem" }}
          />
        )}
        <Flex
          sx={{
            flexDirection: "column",
            ml: 0,
            maxWidth: "15.688rem",
            "@media(max-width: 1024px)": {
              ml: "1.5rem",
            },
            "@media(max-width: 1030px)": {
              maxWidth: "100%",
            },
            ...textContainerCustomStyles,
          }}
        >
          <Text
            sx={{
              ...typography.h6,
              mt: "1.56rem",
              mb: "0.5rem",
              "@media(max-width: 1024px)": {
                mt: 0,
              },
              ...textInnerContainerCustomStyles,
            }}
          >
            {menuData?.title}
          </Text>
          <Text sx={{ ...typography.textMd400, mb: "1.56rem" }}>
            {menuData?.description}
          </Text>
          <NavLink
            onClick={handleClick}
            customStyles={{ color: colors.primaryMain, px: 0 }}
          >
            {menuData?.linkText}
          </NavLink>
        </Flex>
      </Flex>
    </Flex>
  );
};

const Logo = ({ region, isLogoClickable }) => {
  const regionLinks = {
    ME: "/middle-east/",
    "middle-east": "/middle-east/",
    ID: "/indonesia/",
    indonesia: "/indonesia/",
    SG: "/singapore/",
    singapore: "/singapore/",
    MY: "/malaysia/",
    malaysia: "/malaysia/",
    SA: "/saudi-arabia/",
    "saudi-arabia": "/saudi-arabia/",
  };

  const homePageLink = regionLinks[region] || "/";

  return (
    <a href={isLogoClickable && homePageLink}>
      <img
        sx={{ width: ["5rem", "5.625rem", "5.625rem", "7rem"] }}
        src={logo}
        alt="greytHR"
      />
    </a>
  );
};

const getSignUpLink = (type, regionCode, path) => {
  const encoded_path = encodeURIComponent(path);
  const cta_name = type == "H" ? "gc_2001_001" : "gc_2001_005";
  const signupLinkSrc =
    regionCode === "ME" || regionCode === "middle-east"
      ? `/middle-east/signup/?gc_name=${cta_name}&gc_source=wgc%2F${encoded_path}&gc_index=0`
      : `/signup/?gc_name=${cta_name}&gc_source=wgc%2F${encoded_path}&gc_index=0`;
  return signupLinkSrc;
};

// Temporarily hide header "Request a Demo" for these regions.
// To re-enable for a region, remove its code from this list.
const HIDE_HEADER_DEMO_CTA_REGIONS = ["SG"];

const HeaderCTA = ({
  regionCode,
  isHrGarden,
  path,
  displaySecondaryCTA = true,
  displayPrimaryCTA = "/demo/",
  isHomePage = false,
  isLoginPage = false,
}) => {
  const signupLink = getSignUpLink("H", regionCode, path);
  const isMEorIN = regionCode === "IN" || regionCode === "ME";
  const data = getCommonData(regionCode)?.header;
  const showPrimaryCTA =
    Boolean(displayPrimaryCTA) &&
    !HIDE_HEADER_DEMO_CTA_REGIONS.includes(regionCode);

  if (isHrGarden)
    return (
      <Href href="#hr-garden-form">
        <Button variant="filled" id="subscribe_btn">
          Subscribe
        </Button>
      </Href>
    );

  if (isLoginPage) {
    return (
      <Flex sx={{ gap: "1.5rem" }}>
        <Button
          variant="outlined"
          onClick={() => navigate("/greythr-help/")}
          id={`nav_bar_support_${regionCode?.toLowerCase()}_btn`}
        >
          Support
        </Button>
      </Flex>
    );
  }

  const demoButton = showPrimaryCTA && (
    <Button
      key="demo"
      variant="filled"
      id={`nav_bar_demo_${regionCode?.toLowerCase()}_btn`}
      onClick={() => navigateCrossApp(displayPrimaryCTA)}
    >
      {data?.CTAName}
    </Button>
  );

  const CTAConfig = [
    {
      media: "@media (min-width:1031px)",
      buttons: [
        isMEorIN && displaySecondaryCTA && (
          <Button
            key="free"
            variant="outlined"
            id={`nav_bar_free_trial_${regionCode?.toLowerCase()}_btn`}
            onClick={() => navigate(signupLink)}
          >
            Free Trial
          </Button>
        ),
        demoButton,
      ].filter(Boolean),
    },
    {
      media: "@media (min-width:600px) and (max-width:1030px)",
      buttons: isMEorIN
        ? isHomePage
          ? [
              <Button
                key="login"
                variant="outlined"
                id={`nav_bar_login_${regionCode?.toLowerCase()}_btn`}
                onClick={() => navigate("login")}
              >
                Login
              </Button>,
              demoButton,
            ].filter(Boolean)
          : [
              displaySecondaryCTA && (
                <Button
                  key="free"
                  variant="outlined"
                  id={`nav_bar_free_trial_${regionCode?.toLowerCase()}_btn`}
                  onClick={() => navigate(signupLink)}
                >
                  Free Trial
                </Button>
              ),
              demoButton,
            ].filter(Boolean)
        : [demoButton].filter(Boolean),
    },
    {
      media: "@media (max-width:599px)",
      buttons: isMEorIN
        ? [
            isHomePage && showPrimaryCTA && (
              <Button
                key="demo"
                variant="filled"
                id={`nav_bar_${regionCode?.toLowerCase()}_btn`}
                onClick={() => navigateCrossApp(displayPrimaryCTA)}
              >
                Request Demo
              </Button>
            ),
            !isHomePage && displaySecondaryCTA && (
              <Button
                key="free"
                variant="filled"
                id={`nav_bar_${regionCode?.toLowerCase()}_btn`}
                onClick={() => navigate(signupLink)}
              >
                Free Trial
              </Button>
            ),
          ].filter(Boolean)
        : [demoButton].filter(Boolean),
    },
  ];

  return CTAConfig.map(({ media, buttons }, idx) =>
    buttons.length ? (
      <Flex
        key={idx}
        sx={{ gap: "1.5rem", display: "none", [media]: { display: "flex" } }}
      >
        {buttons}
      </Flex>
    ) : null,
  );
};

const Nav = ({
  region,
  menuHeaderItems,
  showHeaderCTA = "true",
  menu,
  path,
  getPopupContainer,
  displaySecondaryCTA,
  displayPrimaryCTA,
  isHomePage,
  isLoginPage,
  showNavosHeroActions = false,
  showAskNavos = true,
}) => {
  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Flex>
        <NavMenu
          region={region}
          menuHeaderItems={menuHeaderItems}
          menu={menu}
          getPopupContainer={getPopupContainer}
        />
      </Flex>
      {showNavosHeroActions ? (
        <NavosHeroActions
          variant="header"
          searchInputId="navos-header-search"
          showAskNavos={showAskNavos}
        />
      ) : (
        showHeaderCTA && (
          <HeaderCTA
            regionCode={region}
            path={path}
            displaySecondaryCTA={displaySecondaryCTA}
            displayPrimaryCTA={displayPrimaryCTA}
            isHomePage={isHomePage}
            isLoginPage={isLoginPage}
          />
        )
      )}
    </Flex>
  );
};

const NavMenu = ({ region, menuHeaderItems, menu, getPopupContainer }) => {
  const pageRegionCode = region || useSelector((state) => state.app.region);
  const [openKeys, setOpenKeys] = useState([]);
  return (
    <Menu
      mode="horizontal"
      sx={styledMenu}
      triggerSubMenuAction="click"
      motion={inlineMotion}
      onOpenChange={(keys) => setOpenKeys(keys)}
      getPopupContainer={getPopupContainer}
    >
      {menuHeaderItems?.menu?.map(
        ({ title, path, regional, hide, items = [] }, index) => {
          const hasSubMenu = items && items.length > 0;
          const promoKey =
            title === "Resources" ||
            title === "Customers" ||
            title === "Company" ||
            title === "Partner" ||
            title === "Produk" ||
            title === "Tentang Kami" ||
            title === "Kemitraan"
              ? "Resource"
              : title;
          const promoData = menu.find(
            (item) => item.region === region && item.menu === promoKey,
          );
          if (hide) {
            return null;
          }
          const isOpen = openKeys.includes(`menu-items-${title}`);
          const parentTitle = (
            <span
              sx={{
                ...typography.h6,
                "&:hover": { color: colors.primaryMain },
                color: isOpen ? colors.primaryMain : colors.black,
                mr: "1.5rem",
              }}
            >
              {title}
              {isOpen ? (
                <ChevronUp size="1.25rem" style={{ marginLeft: "0.25rem" }} />
              ) : (
                <ChevronDown size="1.25rem" style={{ marginLeft: "0.25rem" }} />
              )}
            </span>
          );

          if (hasSubMenu) {
            return (
              <SubMenu
                title={parentTitle}
                key={`menu-items-${title}`}
                mode="horizontal"
              >
                <MenuItemGroup id="flexSubMenuGroup">
                  {items.map((item, index) => {
                    const hasColumns = item.column && item.column.length > 0;

                    if (hasColumns && item.column[0].title === "====") {
                      return (
                        <Divider
                          key={`menu-items-${title}`}
                          className="customDivider"
                        />
                      );
                    }
                    if (hasColumns) {
                      return (
                        <MenuItemGroup
                          className="columnMenuGroup"
                          key={`column-menu-${index}`}
                          sx={{
                            minWidth: "17.438rem", 
                            "@media screen and (min-width: 1031px) and (max-width: 1500px)": {
                              minWidth: "12.625rem", 
                            },
                            padding: "1.23rem 1.2rem 0rem 1.5rem",
                            position: "relative",
                            overflow: "hidden",
                            boxSizing: "border-box",
                            ...(item.dividerBefore && {
                              marginLeft: "1rem",
                              paddingLeft: "2rem",
                              position: "relative",
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                left: 0,
                                top: "1rem",     
                                bottom: "1rem", 
                                width: "1px",
                                backgroundColor: colors.neutral200,
                              },
                            }),
                            ...(item.category === "Product Extend" &&
                              items.some((category) => category.category === "For Industry") && {
                                mr: "1.5rem",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  top: "2rem",
                                  right: 0,
                                  width: "1px",
                                  height: "calc(100% - 2rem)",
                                  backgroundColor: colors.neutral200,
                                },
                            }),
                          }}
                        >
                          {item.category && item.category !== "Competitors" && (
                            <Box
                              sx={{
                                ...typography.h6,
                                py: "0.75rem",
                                px: "0.75rem",
                                ...(item.category === "Product Extend" && {
                                  color: colors.white,
                                }),
                              }}
                            >
                              {item.category}
                            </Box>
                          )}
                          {item.column.map(
                            (
                              {
                                title,
                                path,
                                regional,
                                iconName,
                                subContent = [],
                                imageUrl,
                              },
                              index,
                            ) => {
                              const hasSubContent =
                                subContent && subContent.length > 0;

                              if (title === "---") {
                                return (
                                  <Divider
                                    key={`divider-column-menu-${index}`}
                                    sx={{
                                      marginRight: "-2.93rem",
                                      height: 220,
                                    }}
                                  />
                                );
                              }
                              if (hasSubContent) {
                                return (
                                  <MenuItemGroup
                                    id="menuGroup"
                                    key={`menu-group-${index}`}
                                  >
                                    <MenuItem>
                                      <HeaderLink
                                        to={path}
                                        key={path}
                                        title={title}
                                        region={pageRegionCode}
                                        regional={regional}
                                        iconName={iconName}
                                        isIconRequired={true}
                                      />
                                    </MenuItem>
                                    {/* nested menu-item */}
                                    {subContent.map(
                                      ({ title, path, regional }) => (
                                        <MenuItem key={`sub-menu-${title}`}>
                                          <div
                                            sx={{
                                              ml: "2.62rem",
                                              maxWidth: "calc(100% - 2.62rem)",
                                              overflow: "hidden",
                                              border: `0.5px solid ${colors.white} `,
                                            }}
                                          >
                                            <HeaderLink
                                              to={path}
                                              key={path}
                                              title={title}
                                              region={pageRegionCode}
                                              regional={regional}
                                            />
                                          </div>
                                        </MenuItem>
                                      ),
                                    )}
                                  </MenuItemGroup>
                                );
                              }

                              return (
                                <MenuItem key={`${title}${index}`}>
                                  <HeaderLink
                                    to={path}
                                    key={path}
                                    title={title}
                                    region={pageRegionCode}
                                    regional={regional}
                                    iconName={iconName}
                                    isIconRequired={true}
                                    imageUrl={imageUrl}
                                  />
                                </MenuItem>
                              );
                            },
                          )}
                        </MenuItemGroup>
                      );
                    }
                  })}
                  {promoData && title !== "Product" && title !== "Resources" &&(
                    <PromoSection
                      menuData={promoData}
                      customStyles={{
                        display: "flex",
                        "@media(max-width: 1024px)": {
                          display: "none",
                        },
                        marginLeft: title === "Resources" ? "-3.265rem" : 0,
                      }}
                    />
                  )}
                </MenuItemGroup>
                { promoData && (
                  <PromoSection
                    menuData={promoData}
                    customStyles={{
                      display: "none",
                      "@media(max-width: 1024px)": {
                        display: "flex",
                      },
                    }}
                  />
                )}
              </SubMenu>
            );
          }
          //  if menu has no submenu item
          return (
            <MenuItem key={index}>
              <Box sx={{ mr: "1.5rem" }}>
                <HeaderLink
                  to={path}
                  key={path}
                  title={title}
                  region={pageRegionCode}
                  regional={regional}
                  isNavItem
                />
              </Box>
            </MenuItem>
          );
        },
      )}
    </Menu>
  );
};

const HrGardenNavMenu = ({ region, menuHeaderItems, getPopupContainer }) => {
  const pageRegionCode = region || useSelector((state) => state.app.region);
  return (
    <Flex sx={{ justifyContent: "space-between" }}>
      <Menu
        mode="horizontal"
        sx={styledMenu}
        triggerSubMenuAction="hover"
        motion={inlineMotion}
        getPopupContainer={getPopupContainer}
      >
        {menuHeaderItems?.menu?.map(({ title, hide, items = [] }) => {
          if (hide) {
            return null;
          }

          const hasSubMenu = items && items.length > 0;
          const parentTitle = (
            <span
              sx={{
                ...typography.textMd500,
                "&:hover": { color: colors.primaryMain },
                "@media(min-width: 1065px)": {
                  mr: "1.5rem",
                },
              }}
            >
              {title}
              <ChevronDown size="1.25rem" style={{ marginLeft: "0.5rem" }} />
            </span>
          );

          if (hasSubMenu) {
            return (
              <SubMenu title={parentTitle} mode="horizontal">
                <MenuItemGroup
                  id="flexSubMenuGroup"
                  style={{ width: "21.063rem" }}
                >
                  {items.map((item) => {
                    const hasColumns = item.column && item.column.length > 0;

                    if (hasColumns) {
                      return (
                        <MenuItemGroup
                          className="columnMenuGroup"
                          style={{
                            padding: "1.188rem 1.92rem 0.375rem 1.859rem",
                            width: "17.438rem",
                          }}
                        >
                          {item.column.map(
                            (
                              { title, path, regional, iconName, imageUrl },
                              index,
                            ) => {
                              return (
                                <MenuItem
                                  key={`${title}${index}`}
                                  style={{
                                    width: "17.438rem",
                                  }}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: colors.hoverBackground,
                                    },
                                  }}
                                >
                                  <HeaderLink
                                    to={path}
                                    key={path}
                                    title={title}
                                    region={pageRegionCode}
                                    regional={regional}
                                    iconName={iconName}
                                    isIconRequired={true}
                                    imageUrl={imageUrl}
                                  />
                                </MenuItem>
                              );
                            },
                          )}
                        </MenuItemGroup>
                      );
                    }
                  })}
                </MenuItemGroup>
              </SubMenu>
            );
          }
        })}
      </Menu>
      <Link
        href={"#hr-garden-form"}
        sx={{
          display: "block",
          "@media screen and (min-width: 1000px) and (max-width: 1500px)": {
            display: "none",
          },
        }}
      >
        <Button variant="filled" id="subscribe_btn">
          Subscribe
        </Button>
      </Link>
    </Flex>
  );
};

const SubMenuCard = ({ title, titleStyles, isActive, onClick }) => {
  const theme = isActive ? colors.primaryMain : colors.black;
  return (
    <Flex
      sx={{
        py: "1rem",
        px: "0.75rem",
        gap: "1rem",
        alignItems: "center",
        color: theme,
      }}
      onClick={onClick}
    >
      <Text
        sx={{
          ...typography.h6,
          ml: "0.69rem",
          color: theme,
          ...titleStyles,
        }}
      >
        {title}
      </Text>
      <ChevronRight size="1.25rem" color={theme} />
    </Flex>
  );
};

const BurgerMenuItems = ({
  px,
  menuHeaderItems,
  region,
  keyPrefix = "list",
}) => {
  const pageRegionCode = region || useSelector((state) => state.app.region);
  const [activeCards, setActiveCards] = useState({});
  const sectionRefs = useRef({});

  const toggleCard = (key) => {
    setActiveCards((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  return menuHeaderItems?.menu?.map(
    (
      { title, path, regional = false, hide, items = [], customElement },
      index,
    ) => {
      if (hide === true) return null;
      const hasSubMenu = items && items.length > 0;

      const keyRoot = `${keyPrefix}-${index}`;
      const isActive = activeCards[keyRoot];

      const handleCardClick = () => {
        const willOpen = !isActive;
        toggleCard(keyRoot);
        if (customElement && willOpen) {
          setTimeout(() => {
            const el = sectionRefs.current[keyRoot];
            if (el && typeof el.scrollIntoView === "function") {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 150);
        }
      };
      const renderCard = () => (
        <SubMenuCard
          key={`card-${keyRoot}`}
          title={title}
          titleStyles={
            keyPrefix.includes("region")
              ? { ...typography.h7, ml: 0, color: "inherit" }
              : undefined
          }
          isActive={isActive}
          onClick={handleCardClick}
        />
      );

      if (customElement) {
        return (
          <Box
            key={`custom-${keyRoot}`}
            ref={(el) => (sectionRefs.current[keyRoot] = el)}
          >
            <Accordion as="h3" key={`acc-${keyRoot}`}>
              <Box
                label={title}
                isElement={true}
                Element={renderCard}
                key={`box-${keyRoot}`}
                isOpen={false}
                style={{
                  px: 0,
                  m: 0,
                  py: 0,
                  cursor: "pointer",
                  pb: "3rem",
                  "@media (max-width: 375px)": {
                    pb: "5rem",
                  },
                }}
              >
                {customElement}
              </Box>
            </Accordion>
          </Box>
        );
      }
      if (hasSubMenu) {
        return (
          <Box key={`menu-${keyRoot}`}>
            <Accordion as="h3" key={`acc-${keyRoot}`}>
              <Box
                label={title}
                isElement={true}
                Element={renderCard}
                key={`box-${keyRoot}`}
                isOpen={false}
                style={{
                  px: 0,
                  m: 0,
                  py: 0,
                  cursor: "pointer",
                }}
              >
                {items.map((item, i) => {
                  const hasColumns = item.column && item.column.length > 0;
                  if (hasColumns) {
                    return (
                      <Box key={`submenu-${i}`}>
                        {item.category && (
                          <Box
                            sx={{
                              ...typography.h6,
                              py: "1rem",
                              px: "0.75rem",
                              mx: "0.625rem",
                            }}
                          >
                            {item.category}
                          </Box>
                        )}
                        {item.column.map((columnItem, index) => {
                          const {
                            title,
                            path,
                            subContent = [],
                            iconName,
                            imageUrl,
                            regional: itemRegional,
                          } = columnItem;
                          const hasSubContent =
                            subContent && subContent.length > 0;
                          if (title !== "====") {
                            return (
                              <Box key={"sub-menu-" + index}>
                                <HeaderLink
                                  to={path}
                                  region={pageRegionCode}
                                  regional={itemRegional}
                                  key={path}
                                  title={title}
                                  px={10}
                                  vertical
                                  isIconRequired={true}
                                  iconName={iconName}
                                  isMobile
                                  imageUrl={imageUrl}
                                />
                                {hasSubContent
                                  ? subContent.map((c) => (
                                      <HeaderLink
                                        to={c.path}
                                        region={pageRegionCode}
                                        regional={c.regional}
                                        key={c.path}
                                        title={c.title}
                                        px={4}
                                        vertical
                                      />
                                    ))
                                  : null}
                              </Box>
                            );
                          }
                        })}{" "}
                      </Box>
                    );
                  }
                })}
              </Box>
            </Accordion>
            {<Box sx={burgerMenuItemsStyles.divider} />}
          </Box>
        );
      }
      return (
        <Box key={`menu-${index}`}>
          <HeaderLink
            to={path}
            region={pageRegionCode}
            regional={regional}
            key={path}
            title={title}
            px={px}
            vertical
            color={colors.black}
          />
          {<Box sx={burgerMenuItemsStyles.divider} />}
        </Box>
      );
    },
  );
};

const BurgerMenu = ({
  menuHeaderItems,
  region,
  menu,
  isHrGarden,
  path,
  displayPrimaryCTA,
  displaySecondaryCTA,
  isHomePage,
  isLoginPage,
  displayBannerLoginCTA = false,
  displayBannerSupportCTA = true,
}) => {
  if (isHrGarden)
    return (
      <HrGardenBurgerMenu menuHeaderItems={menuHeaderItems} region={region} />
    );
  const promoData = menu.find(
    (item) => item.region === region && item.menu === "Product",
  );

  return (
    <div sx={{ p: 9, mx: 3 }}>
      <SlideMenu
        right
        styles={burgerMenuStyles}
        customBurgerIcon={
          <img
            src={burgerMenuIcon}
            alt="burger-menu"
            sx={{ width: "1.813rem" }}
          />
        }
        customCrossIcon={
          <img src={crossIcon} alt="cross-menu" sx={{ width: "32px" }} />
        }
      >
        <Flex>
          <Flex style={burgerMenuItemsStyles.logoContainer}>
            <Logo region={region} />
          </Flex>
          <Flex
            sx={{
              position: "absolute",
              right: "4rem",
              top: 13,
            }}
          >
            <HeaderCTA
              regionCode={region}
              isHrGarden={isHrGarden}
              path={path}
              displayPrimaryCTA={displayPrimaryCTA}
              displaySecondaryCTA={displaySecondaryCTA}
              isHomePage={isHomePage}
              isLoginPage={isLoginPage}
            />
          </Flex>
        </Flex>

        <Flex style={{ marginTop: "5.81rem", justifyContent: "space-between" }}>
          <BurgerMenuItems
            keyPrefix="main-menu"
            menuHeaderItems={menuHeaderItems}
            region={region}
          />
          <Flex sx={{ flexDirection: "column" }}>
            {displayBannerLoginCTA && (region === "IN" || region === "ME") && (
              <Href
                href="login/"
                aria-label="Log in to your account"
                sx={linkStyles}
              >
                Login
              </Href>
            )}
            {displayBannerSupportCTA && region === "IN" && (
              <Href
                href="/greythr-help/"
                aria-label="Visit our support page"
                sx={linkStyles}
              >
                Support
              </Href>
            )}
          </Flex>
          <BurgerMenuItems
            keyPrefix="region"
            menuHeaderItems={regionMenuHeaderItems}
            region={region}
          />
          {promoData && (
            <PromoSection
              menuData={promoData}
              customStyles={burgerMenuItemsStyles.promoSection}
            />
          )}
        </Flex>
      </SlideMenu>
    </div>
  );
};

const HrGardenBurgerMenu = ({ menuHeaderItems, region }) => {
  return (
    <div sx={{ p: 9, mx: 3 }}>
      <SlideMenu
        right
        styles={{ ...burgerMenuStyles, bmMenu: { height: "100%" } }}
        customBurgerIcon={
          <img
            src={burgerMenuIcon}
            alt="burger-menu"
            sx={{ width: "1.813rem" }}
          />
        }
        customCrossIcon={
          <img src={crossIcon} alt="cross-menu" sx={{ width: "32px" }} />
        }
      >
        <Flex style={burgerMenuItemsStyles.logoContainer}>
          <Logo region={region} />
        </Flex>
        <Flex style={{ marginTop: "5.81rem", justifyContent: "space-between" }}>
          <BurgerMenuItems menuHeaderItems={menuHeaderItems} />
        </Flex>
      </SlideMenu>
    </div>
  );
};

const HeaderLink = ({
  to,
  title,
  px,
  region,
  regional = true,
  vertical = false,
  isIconRequired = false,
  iconName,
  isNavItem,
  isMobile = false,
  imageUrl,
}) => {
  const sx = {
    px,
    pb: 0,
    width: isNavItem ? ["100%", "100%", "auto", "auto"] : "100%",
    maxWidth: "100%",
    display: "block",
    "&:hover": {
      textDecoration: "none",
    },
  };
  if (vertical) {
    sx.display = "block";
  }
  return to && to.split("/")[0] === "https:" ? (
    <Href
      href={to}
      target="blank"
      style={{
        paddingLeft: isMobile ? "10px" : 0,
        textDecoration: "none",
        ...sx,
      }}
    >
      <ListCard
        iconName={isIconRequired ? iconName : null}
        title={title}
        isChevronRequired={false}
        hoverTextColor={colors.primaryMain}
        hoverBgColor={colors.primary100}
        imageUrl={imageUrl}
        customStyles={{
          minWidth: "17.4375rem",
        }}
      />
    </Href>
  ) : (
    <RegionalLink
      sx={sx}
      to={to}
      region={region}
      regional={regional}
      variant="header"
    >
      {isNavItem ? (
        <span
          sx={{
            ...typography.h6,
            color: colors.black,
            "&:hover": { color: colors.primaryMain },
          }}
        >
          {title}
        </span>
      ) : (
        <ListCard
          iconName={isIconRequired ? iconName : null}
          title={title}
          isChevronRequired={false}
          hoverTextColor={colors.primaryMain}
          hoverBgColor={colors.primary100}
          imageUrl={imageUrl}
        />
      )}
    </RegionalLink>
  );
};

const MobileMenuHolder = ({
  menu,
  region,
  isHrGarden,
  showMobileMenu,
  path,
  displayPrimaryCTA = "/demo/",
  displaySecondaryCTA = true,
  isHomePage = false,
  isLoginPage = false,
  showNavosHeroActions = false,
  showAskNavos = true,
  showHeaderCTA = true,
}) => {
  if (!showMobileMenu && !showNavosHeroActions) return null;

  return (
    <Flex sx={{ justifyContent: "flex-end", alignItems: "center", gap: "0.75rem" }}>
      {showNavosHeroActions ? (
        <NavosHeroActions
          variant="header"
          compact
          searchInputId="navos-header-search-mobile"
          showAskNavos={showAskNavos}
        />
      ) : (
        showHeaderCTA && (
          <HeaderCTA
            regionCode={region}
            isHrGarden={isHrGarden}
            path={path}
            displayPrimaryCTA={displayPrimaryCTA}
            displaySecondaryCTA={displaySecondaryCTA}
            isHomePage={isHomePage}
            isLoginPage={isLoginPage}
          />
        )
      )}
      {menu}
    </Flex>
  );
};

const Header = ({
  region,
  menuHeaderItems,
  menuItems,
  showHeaderCTA,
  isHrGarden,
  isMenuRequired = true,
  newFeatureMenuItem,
  newFeatureMenuItemID,
  displaySecondaryCTA,
  displayPrimaryCTA,
  isHomePage,
  isLoginPage,
  displayBannerLoginCTA,
  displayBannerSupportCTA,
  menuPromoData,
  showNavosHeroActions = false,
  showAskNavos = true,
}) => {
  const path = history.location.pathname;
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const headerContainerRef = useRef(null);
  const bpIndex = useBreakpointIndex({ defaultIndex: 1 });
  region = region || useSelector((state) => state.app.region);
  menuItems = menuItems ?? (menuHeaderItems ? menuHeaderItems[region] : null);

  const nodes = menuPromoData?.nodes || [];
  useEffect(() => {
    const updateShowMobileMenu = () => {
      setShowMobileMenu(window.innerWidth <= 1030);
    };
    updateShowMobileMenu();
    window.addEventListener("resize", updateShowMobileMenu);

    return function cleanup() {
      window.removeEventListener("resize", updateShowMobileMenu);
    };
  }, []);

  const shouldRenderMenu = !(
    path === "/signup/" ||
    path === "/signup" ||
    path === "/email-verify" ||
    path === "/email-verify/"
  );

  const mobileMenu = (
    <BurgerMenu
      region={region}
      menuHeaderItems={menuItems}
      showHeaderCTA={showHeaderCTA}
      menu={nodes}
      isHrGarden={isHrGarden}
      path={path}
      displayPrimaryCTA={displayPrimaryCTA}
      displaySecondaryCTA={displaySecondaryCTA}
      isHomePage={isHomePage}
      isLoginPage={isLoginPage}
      displayBannerLoginCTA={displayBannerLoginCTA}
      displayBannerSupportCTA={displayBannerSupportCTA}
    />
  );
  // This is the popup container for the menu. Created to resolve the issue of the dropdown menu drifting on page scroll.
  const getPopupContainer = () => headerContainerRef.current || document.body;

  return (
    <StyledHeaderContainer
      ref={headerContainerRef}
      style={{
        backgroundColor: colors.white,
      }}
      as="div"
    >
      <StyledHeaderNav
        as="nav"
        style={{
          zIndex: 99,
          maxWidth: "97.5rem",
          margin: "auto",
          boxSizing: "content-box",
        }}
      >
        <Logo region={region} isLogoClickable={isMenuRequired} />
        {isMenuRequired && shouldRenderMenu && (
          <Box sx={{ width: "100%" }}>
            {showMobileMenu || bpIndex <= 2 ? (
              <MobileMenuHolder
                shouldRenderMenu
                menu={mobileMenu}
                region={region}
                isHrGarden={isHrGarden}
                showMobileMenu={showMobileMenu}
                displaySecondaryCTA={displaySecondaryCTA}
                displayPrimaryCTA={displayPrimaryCTA}
                path={path}
                isHomePage={isHomePage}
                isLoginPage={isLoginPage}
                showNavosHeroActions={showNavosHeroActions}
                showAskNavos={showAskNavos}
                showHeaderCTA={showHeaderCTA}
              />
            ) : (
              bpIndex > 2 && (
                <Box>
                  {isHrGarden ? (
                    <HrGardenNavMenu
                      region={region}
                      menuHeaderItems={menuItems}
                      showHeaderCTA={showHeaderCTA}
                      menu={nodes}
                      getPopupContainer={getPopupContainer}
                    />
                  ) : (
                    <Nav
                      region={region}
                      menuHeaderItems={menuItems}
                      showHeaderCTA={showHeaderCTA}
                      menu={nodes}
                      path={path}
                      getPopupContainer={getPopupContainer}
                      newFeatureMenuItem={newFeatureMenuItem}
                      newFeatureMenuItemID={newFeatureMenuItemID}
                      displaySecondaryCTA={displaySecondaryCTA}
                      displayPrimaryCTA={displayPrimaryCTA}
                      isHomePage={isHomePage}
                      isLoginPage={isLoginPage}
                      showNavosHeroActions={showNavosHeroActions}
                      showAskNavos={showAskNavos}
                    />
                  )}
                </Box>
              )
            )}
          </Box>
        )}
      </StyledHeaderNav>
    </StyledHeaderContainer>
  );
};

const RegionSelectorMenu = () => {
  const dispatch = useDispatch();
  const region = useSelector((state) => state.app.region);

  const handleRegionClick = (code, path) => {
    dispatch(setPageRegion(code));
    if (!path) {
      // IN (main site root): hard navigation so CloudFront serves main site index.html
      window.location.assign("/");
      return;
    }
    // Navigate to the region-specific path for other countries
    navigate(`/${path}`);
  };

  return (
    <Box sx={{ pb: "3rem" }}>
      {SUPPORTED_REGIONS.map(({ code, name, path }) => (
        <Box
          key={`region-${code.toLowerCase()}`}
          sx={{ cursor: "pointer", mb: 1, borderRadius: "6px" }}
          onClick={() => handleRegionClick(code, path)}
        >
          <ListCard
            title={name}
            isChevronRequired={false}
            customStyles={{
              px: "0.75rem",
              py: "1rem",
              transition: "background 0.2s",
              maxWidth: "16.688rem",
            }}
            hoverBgColor={colors.primary100}
            hoverTextColor={colors.primaryMain}
          />
        </Box>
      ))}
    </Box>
  );
};

const regionMenuHeaderItems = {
  menu: [
    {
      title: "Change Region",
      customElement: <RegionSelectorMenu />,
    },
  ],
};

export default Header;
